type LocationLike = Pick<
  Location,
  "protocol" | "hostname" | "port" | "search" | "hash" | "origin"
>;

const isLoopbackHost = (hostname: string) =>
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname === "::1" ||
  hostname === "[::1]";

const copySearchParams = (source: string, target: URL) => {
  const params = new URLSearchParams(source);
  params.forEach((value, key) => target.searchParams.append(key, value));
};

/**
 * Keeps the localhost-only admin marker when an admin page replaces its query
 * string. Deployed admin pages derive their context from the hostname and do
 * not need the marker.
 */
export const preserveAdminDevSubdomain = (
  current: URLSearchParams,
  next: URLSearchParams,
): URLSearchParams => {
  if (current.get("subdomain") === "admin") {
    next.set("subdomain", "admin");
  }
  return next;
};

/**
 * Builds an admin-app URL without inventing invalid loopback subdomains such
 * as admin.127.0.0.1. Local development uses App's existing `subdomain=admin`
 * escape hatch; deployed hosts keep the real admin subdomain.
 */
export const buildAdminUrl = (
  location: LocationLike,
  pathname = "/",
): string => {
  if (isLoopbackHost(location.hostname)) {
    const target = new URL(pathname, location.origin);
    copySearchParams(location.search, target);
    target.searchParams.set("subdomain", "admin");
    target.hash = location.hash;
    return target.toString();
  }

  const host = location.hostname
    .replace(/^admin\./, "")
    .replace(/^internal\./, "")
    .replace(/^www\./, "");
  const port = location.port ? `:${location.port}` : "";
  return `${location.protocol}//admin.${host}${port}${pathname}${location.search}${location.hash}`;
};

/**
 * Opens the admin app with a one-time collaborator-business selection. The
 * admin AuthContext still verifies that the signed-in user has an active grant
 * for this business before activating it.
 */
export const buildCollaboratorDashboardUrl = (
  location: LocationLike,
  businessId: string,
): string => {
  const target = new URL(buildAdminUrl(location, "/"));
  target.searchParams.set("collaborator_business", businessId);
  return target.toString();
};

export const buildPublicUrl = (
  location: LocationLike,
  pathname = "/",
): string => {
  if (isLoopbackHost(location.hostname)) {
    const target = new URL(pathname, location.origin);
    copySearchParams(location.search, target);
    target.searchParams.delete("subdomain");
    target.hash = location.hash;
    return target.toString();
  }

  const host = location.hostname.replace(/^admin\./, "").replace(/^www\./, "");
  const port = location.port ? `:${location.port}` : "";
  return `${location.protocol}//${host}${port}${pathname}${location.search}${location.hash}`;
};

// Old dashboard emails linked `?section=<name>` (some on the public host); the
// dashboard reads `?tab=`. Names that changed since are mapped here.
const LEGACY_SECTION_PARAMS: Record<string, Record<string, string>> = {
  automations: { tab: "workflows" },
  billing: { tab: "settings", settings: "billing" },
};

/**
 * The `?tab=` query an old `?section=` dashboard link meant, or null when the
 * URL has no legacy section (or already says which tab to open).
 */
export const legacySectionParams = (search: URLSearchParams): URLSearchParams | null => {
  const section = search.get("section")?.trim();
  if (!section || search.get("tab")) return null;
  const next = new URLSearchParams(search);
  next.delete("section");
  for (const [key, value] of Object.entries(LEGACY_SECTION_PARAMS[section] ?? { tab: section })) {
    next.set(key, value);
  }
  return next;
};
